import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback
} from "react";
import { createPortal } from "react-dom";

/**
 * Popover — Warm Precision design-system primitive for floating menus.
 *
 * Use for: dropdown menus, action menus (kebab), sort/group selectors, filter
 * pickers. Not for: full modals (use <ModalShell>), not for: inline tooltips
 * (we don't have a tooltip primitive yet — coming separately).
 *
 * Pattern:
 *   const ref = useRef<HTMLButtonElement>(null);
 *   const [open, setOpen] = useState(false);
 *   <IconButton ref={ref} onClick={() => setOpen(o => !o)} aria-label="More">…</IconButton>
 *   <Popover open={open} onClose={() => setOpen(false)} anchor={ref} placement="bottom-end">
 *     <Popover.Item onClick={...}>Edit</Popover.Item>
 *     <Popover.Item onClick={...}>Duplicate</Popover.Item>
 *     <Popover.Divider />
 *     <Popover.Item destructive onClick={...}>Delete</Popover.Item>
 *   </Popover>
 *
 * Renders into document.body via a portal so the menu can escape any parent
 * overflow:hidden / transform context. Closes on Esc or click outside. First
 * interactive child receives focus on open; focus returns to the anchor on
 * close.
 */

type Placement =
  | "bottom-start"
  | "bottom-end"
  | "bottom-center"
  | "top-start"
  | "top-end"
  | "top-center";

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  anchor: React.RefObject<HTMLElement>;
  placement?: Placement;
  /** Pixel gap between anchor and popover. Default 6. */
  offset?: number;
  /** Minimum width in px. Default 180. */
  minWidth?: number;
  className?: string;
  children?: React.ReactNode;
}

interface Coords {
  top: number;
  left: number;
}

function computeCoords(
  rect: DOMRect,
  popRect: { width: number; height: number },
  placement: Placement,
  offset: number
): Coords {
  let top = 0;
  let left = 0;
  const isTop = placement.startsWith("top");
  top = isTop ? rect.top - popRect.height - offset : rect.bottom + offset;
  if (placement.endsWith("start")) left = rect.left;
  else if (placement.endsWith("end")) left = rect.right - popRect.width;
  else left = rect.left + (rect.width - popRect.width) / 2;
  // Clamp to viewport.
  const pad = 8;
  if (left < pad) left = pad;
  if (left + popRect.width > window.innerWidth - pad) {
    left = window.innerWidth - popRect.width - pad;
  }
  if (top < pad) top = pad;
  if (top + popRect.height > window.innerHeight - pad) {
    top = window.innerHeight - popRect.height - pad;
  }
  return { top: top + window.scrollY, left: left + window.scrollX };
}

function Popover({
  open,
  onClose,
  anchor,
  placement = "bottom-start",
  offset = 6,
  minWidth = 180,
  className = "",
  children
}: PopoverProps) {
  const popRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<Coords | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  // Position the popover relative to the anchor.
  useLayoutEffect(() => {
    if (!open) return;
    const anchorEl = anchor.current;
    const popEl = popRef.current;
    if (!anchorEl || !popEl) return;
    const reposition = () => {
      const rect = anchorEl.getBoundingClientRect();
      const popRect = { width: popEl.offsetWidth, height: popEl.offsetHeight };
      setCoords(computeCoords(rect, popRect, placement, offset));
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, anchor, placement, offset]);

  // Focus management.
  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    const popEl = popRef.current;
    if (popEl) {
      const focusable = popEl.querySelector<HTMLElement>(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }
    return () => {
      previousFocus.current?.focus?.();
    };
  }, [open]);

  // Esc + click outside.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (anchor.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, onClose, anchor]);

  if (!open) return null;
  const style: React.CSSProperties = {
    position: "absolute",
    top: coords?.top ?? -9999,
    left: coords?.left ?? -9999,
    minWidth,
    visibility: coords ? "visible" : "hidden",
    zIndex: 1000
  };
  return createPortal(
    <div
      ref={popRef}
      role="menu"
      style={style}
      className={`rounded-lg bg-white border border-zinc-200 shadow-lg py-1 ${className}`}
    >
      {children}
    </div>,
    document.body
  );
}

interface ItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  destructive?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const Item = React.forwardRef<HTMLButtonElement, ItemProps>(function Item(
  { destructive, icon, className = "", children, ...rest },
  ref
) {
  const color = destructive
    ? "text-red-600 hover:bg-red-50"
    : "text-zinc-800 hover:bg-zinc-100";
  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${color} disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:bg-zinc-100 ${className}`}
      {...rest}
    >
      {icon && (
        <span className="shrink-0 w-4 h-4 flex items-center">{icon}</span>
      )}
      <span className="flex-1 truncate">{children}</span>
    </button>
  );
});

function Divider() {
  return <div className="my-1 border-t border-zinc-200" />;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
      {children}
    </div>
  );
}

Popover.Item = Item;
Popover.Divider = Divider;
Popover.Label = Label;

export default Popover;
